/* Which pump of theirs is coming — the pump company lists its units in the Partner Portal,
   and dispatch can add one here on their behalf so nobody waits on the pumper to log in. */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const ADD = "__add__";
const selectCls = "w-full mt-1 rounded-none bg-transparent border border-industrial mono text-sm p-2";
const inputCls = "w-full rounded-none bg-transparent border border-industrial mono text-sm p-2";

export const PumpUnitSelect = ({ companyId, value, onChange, testId = "job-pump-unit" }) => {
  const [units, setUnits] = useState([]);
  const [adding, setAdding] = useState(false);
  const [f, setF] = useState({ name: "", operator_name: "", phone: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!companyId) { setUnits([]); return; }
    try { const r = await api.get(`/pump-companies/${companyId}/pump-units`); setUnits(r.data.units || []); }
    catch { setUnits([]); }
  }, [companyId]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!f.name.trim()) return toast.error("Give the pump a name, e.g. Boom 32m");
    setBusy(true);
    try {
      const r = await api.post(`/pump-companies/${companyId}/pump-units`, f);
      toast.success(`${r.data.name} added — their operator QR is ready in the Partner Portal`);
      setF({ name: "", operator_name: "", phone: "" });
      setAdding(false);
      await load();
      onChange(r.data.id);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not add the pump"); }
    finally { setBusy(false); }
  };

  if (!companyId) return null;

  return (
    <div className="mt-2">
      <label className="label-tag">Which pump (optional)</label>
      <select
        data-testid={testId}
        value={value ?? ""}
        onChange={(e) => {
          if (e.target.value === ADD) { setAdding(true); return; }
          onChange(e.target.value);
        }}
        className={selectCls}
      >
        <option value="">— company-wide (any of their pumps) —</option>
        {units.map((u) => (
          <option key={u.id} value={u.id}>
            {u.name}{u.operator_name ? ` · ${u.operator_name}` : ""}{u.phone ? ` · ${u.phone}` : ""}
          </option>
        ))}
        <option value={ADD}>+ Add a pump…</option>
      </select>

      {adding ? (
        <div className="mt-2 border border-primary-orange p-3 space-y-2" data-testid={`${testId}-add`}>
          <div className="label-tag text-primary-orange">Add one of their pumps</div>
          <input data-testid={`${testId}-add-name`} value={f.name} autoFocus placeholder="Pump name — e.g. Boom 32m"
            onChange={(e) => setF({ ...f, name: e.target.value })} className={inputCls} />
          <div className="grid grid-cols-2 gap-2">
            <input data-testid={`${testId}-add-operator`} value={f.operator_name} placeholder="Operator"
              onChange={(e) => setF({ ...f, operator_name: e.target.value })} className={inputCls} />
            <input data-testid={`${testId}-add-phone`} value={f.phone} placeholder="Mobile"
              onChange={(e) => setF({ ...f, phone: e.target.value })} className={inputCls} />
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={add} disabled={busy} data-testid={`${testId}-add-save`}
              className="bg-primary-orange text-black font-black uppercase tracking-widest text-[11px] px-4 py-2 disabled:opacity-50">
              {busy ? "Saving…" : "Save pump"}
            </button>
            <button type="button" onClick={() => setAdding(false)} data-testid={`${testId}-add-cancel`}
              className="mono text-[11px] uppercase tracking-widest border border-industrial px-4 text-muted-industrial hover:text-ink">Cancel</button>
          </div>
        </div>
      ) : (
        <div className="text-[10px] mono uppercase tracking-widest text-muted-industrial mt-1" data-testid={`${testId}-hint`}>
          {units.length === 0
            ? "No pumps listed yet — pick “Add a pump” and enter it yourself, or they can add it in their portal"
            : "That pump's own QR tracker will show only this job, and the crew lands on the driver's screen"}
        </div>
      )}
    </div>
  );
};
